import { ChevronRight, EllipsisVertical, History } from "lucide-react";
import { useState, type ReactElement } from "react";

import { cn } from "~/lib/utils";

/** A room's retained memory. Browsing history never reopens its lifecycle or engagement. */
export interface ParkedSessionRow {
  readonly sessionId: string;
  readonly label: string;
}

export function ParkedRoomCard({
  roomId,
  label,
  archived = false,
  sessions,
  onBrowseRoom,
  onBrowseSession,
  onMenu,
  onRowMenu,
}: {
  readonly roomId: string;
  readonly label: string;
  /** The room itself is archived: its retired sessions still list here, wearing that state. */
  readonly archived?: boolean;
  readonly sessions: ReadonlyArray<ParkedSessionRow>;
  readonly onBrowseRoom: () => void;
  readonly onBrowseSession: (sessionId: string) => void;
  readonly onMenu: (position: { x: number; y: number }) => void;
  readonly onRowMenu: (sessionId: string, position: { x: number; y: number }) => void;
}): ReactElement {
  const [open, setOpen] = useState(true);
  const title = `${label}${archived ? " · archived room" : ""} · ${sessions.length} retired`;
  return (
    <div
      className="flex w-full min-w-0 flex-col rounded-[10px] bg-[color-mix(in_oklab,var(--ncl-text,currentColor)_2.5%,transparent)] shadow-[inset_0_0_0_1px_color-mix(in_oklab,var(--muted-foreground,currentColor)_22%,transparent)]"
      data-open={open ? "true" : "false"}
      data-room-rail-retired-room={roomId}
    >
      <div
        aria-expanded={open}
        aria-label={title}
        className="group/parked flex h-7 w-full min-w-0 cursor-pointer items-center gap-1.5 rounded-[10px] px-2 text-xs text-muted-foreground outline-hidden transition-colors hover:text-foreground focus-visible:ring-1 focus-visible:ring-ring"
        onClick={() => setOpen((prev) => !prev)}
        onContextMenu={(event) => {
          event.preventDefault();
          onMenu({ x: event.clientX, y: event.clientY });
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setOpen((prev) => !prev);
          }
        }}
        role="button"
        tabIndex={0}
        title={title}
      >
        <span
          aria-hidden="true"
          className="flex size-3.5 shrink-0 items-center justify-center"
          data-room-rail-fold={`retired:${roomId}`}
        >
          <ChevronRight
            className={cn(
              "size-3 transition-transform duration-[var(--duration-quick,150ms)] ease-[var(--ease-smooth-out,ease-out)] motion-reduce:transition-none",
              open && "rotate-90",
            )}
          />
        </span>
        <span className="min-w-0 flex-1 truncate">{label}</span>
        {archived ? (
          <span
            className="font-mono text-[9px] uppercase tracking-[0.08em] opacity-70"
            data-room-rail-record-state="archived-room"
          >
            archived
          </span>
        ) : null}
        <span className="font-mono text-[9px] uppercase tracking-[0.08em] opacity-70">retired</span>
        <span
          aria-hidden="true"
          className="hidden size-3.5 shrink-0 items-center justify-center rounded-sm opacity-60 hover:bg-accent hover:opacity-100 group-hover/parked:inline-flex group-focus-within/parked:inline-flex"
          data-room-rail-browse-room={roomId}
          onClick={(event) => {
            event.stopPropagation();
            onBrowseRoom();
          }}
          title="Browse room history"
        >
          <History className="size-3" />
        </span>
        <span
          aria-hidden="true"
          className="hidden size-3.5 shrink-0 items-center justify-center rounded-sm opacity-60 hover:bg-accent hover:opacity-100 group-hover/parked:inline-flex group-focus-within/parked:inline-flex"
          data-room-rail-menu={`retired:${roomId}`}
          onClick={(event) => {
            event.stopPropagation();
            const rect = event.currentTarget.getBoundingClientRect();
            onMenu({ x: rect.left, y: rect.bottom + 4 });
          }}
        >
          <EllipsisVertical className="size-3" />
        </span>
      </div>
      <div className="t-acc" data-open={open ? "true" : "false"}>
        <div className="t-acc-inner">
          <div className="flex flex-col gap-0.5 px-1 pb-1.5">
            {sessions.map((session) => (
              <button
                aria-label={`${session.label} · retired`}
                className="group/row flex h-6 w-full min-w-0 items-center gap-1.5 rounded-lg py-0 pl-5 pr-1.5 text-left text-[11px] text-muted-foreground outline-hidden transition-colors hover:bg-accent/40 hover:text-foreground focus-visible:ring-1 focus-visible:ring-ring"
                data-room-rail-session={session.sessionId}
                data-room-rail-session-retired=""
                key={session.sessionId}
                onClick={() => onBrowseSession(session.sessionId)}
                onContextMenu={(event) => {
                  event.preventDefault();
                  onRowMenu(session.sessionId, { x: event.clientX, y: event.clientY });
                }}
                title={`${session.label} · retired — browse history`}
                type="button"
              >
                <span
                  aria-hidden="true"
                  className="size-1 shrink-0 rounded-full bg-current opacity-70"
                />
                <span className="min-w-0 flex-1 truncate">{session.label}</span>
                <span
                  aria-hidden="true"
                  className="hidden size-3.5 shrink-0 items-center justify-center rounded-sm opacity-60 hover:bg-accent hover:opacity-100 group-hover/row:inline-flex group-focus-within/row:inline-flex"
                  data-room-rail-browse-history={session.sessionId}
                  onClick={(event) => {
                    event.stopPropagation();
                    onBrowseSession(session.sessionId);
                  }}
                  title="Browse history"
                >
                  <History className="size-3" />
                </span>
                <span
                  aria-hidden="true"
                  className="hidden size-3.5 shrink-0 items-center justify-center rounded-sm opacity-60 hover:bg-accent hover:opacity-100 group-hover/row:inline-flex group-focus-within/row:inline-flex"
                  data-room-rail-menu={session.sessionId}
                  onClick={(event) => {
                    event.stopPropagation();
                    const rect = event.currentTarget.getBoundingClientRect();
                    onRowMenu(session.sessionId, { x: rect.left, y: rect.bottom + 4 });
                  }}
                >
                  <EllipsisVertical className="size-3" />
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
